import React from 'react';
import { bandForHour } from '../../utils/housePartOfDay';

/**
 * 🌦️🕯️ HouseAmbientLayers — Wave 1 of HOUSE_AMBIENCE_TRIO_SPEC_2026_09_15
 * (Lane B day-part wash + Lane C TV glow; both RULED 09-15).
 *
 * Two standing color layers behind the room content, mounted beside
 * AmbientLayer at the same fixed z0 / pointerEvents:none level:
 *   · wash — ONE background-color at ≤0.156 opacity keyed to the house band.
 *     Pure CSS, zero per-frame cost; band changes crossfade over 45s.
 *   · glow — the ruled "ambient spill": a radial blue-white gradient from the
 *     media corner, rendered ONLY while the backend says the house TV actually
 *     resolved something (house.tv.on — the Lane C rider), night-modulated via
 *     the band's glowMod, desktop-only v1 (the phone chat view has no media
 *     corner; AMBIENCE_TRIO §8).
 *
 * Perf contract (inherits the repo rules): opacity/background only — NO
 * animated blur or box-shadow ever (PERF_TODO); flicker is a slow opacity
 * keyframe; prefers-reduced-motion gets a static glow and instant wash.
 * The muddy-smudge guard: the wash tints the whole backdrop at whisper
 * opacity and never gets its own per-element tint on cards or controls.
 *
 * Payload-gated by the CALLER: this renders only when the house block ships a
 * circadian clock (house-v2 spaces), so every other space is byte-identical.
 * Kill switch: remove the single mount in GameStepPage.
 */
export default function HouseAmbientLayers({ hour, tv, isMobile, washScale = 1 }) {
  const band = bandForHour(hour);
  if (!band) return null;
  // washScale (09-17/18 haze fix): while a room's art is the wallpaper the
  // full-page tint reads as fog over it (the golden-hour orange especially);
  // the caller scales it down (roomView.WASH_OVER_ART). 1 = the ruled Lane B.
  const washOpacity = Math.min(0.2, 0.10 * band.mult) * Math.max(0, Math.min(1, Number(washScale) || 0));
  const glowStrength = 0.55 * band.glowMod;
  const glowOn = !!(tv && tv.on) && !isMobile && glowStrength > 0.05;
  return (
    <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
      <style>{`
        .house-daypart-wash{position:absolute;inset:0;transition:background-color 45s linear,opacity 45s linear}
        .house-tv-glow{position:absolute;inset:0;animation:houseTvFlicker 5.6s ease-in-out infinite}
        @keyframes houseTvFlicker{
          0%,100%{opacity:var(--house-glow-o,.4)}
          9%{opacity:calc(var(--house-glow-o,.4)*.84)}
          14%{opacity:var(--house-glow-o,.4)}
          43%{opacity:calc(var(--house-glow-o,.4)*.92)}
          49%{opacity:calc(var(--house-glow-o,.4)*1.05)}
          78%{opacity:calc(var(--house-glow-o,.4)*.88)}
        }
        @media (prefers-reduced-motion: reduce){
          .house-daypart-wash{transition:none}
          .house-tv-glow{animation:none;opacity:var(--house-glow-o,.4)}
        }
      `}</style>
      <div
        className="house-daypart-wash"
        style={{ backgroundColor: band.wash, opacity: washOpacity }}
      />
      {glowOn && (
        <div
          className="house-tv-glow"
          style={{
            '--house-glow-o': glowStrength,
            background: `radial-gradient(46% 34% at 82% 88%, rgba(156,200,234,${(glowStrength * 0.85).toFixed(3)}), rgba(156,200,234,0) 70%)`,
          }}
        />
      )}
    </div>
  );
}
